import { campaignLineStatuses } from '../campaign-editor/constants';
import { COLORS } from '../utils/constants';
import convertToOption, {
  convertToEnumWithOptions,
} from '../utils/convert-to-option';
import { getYesNoValue } from '../utils/create-csv';
import {
  getCoinvestSum,
  getCollaboratorNumbers,
  getNumberOfLinesWithStatus,
  getOpsCompliancePercentage,
  getUniqueGenders,
  getUserNumber,
} from './utils/utils';

const CREATING = 'Creating';
const CREATION_FAILED = 'Creation failed'; // small f here is unconsistent, but changing it in the BE would cause downstream problems
const SETUP_PROGRESS = 'In Progress';
const RUNNING = 'Running';
const PAUSED = 'Paused';
const ENDING_REQUESTED = 'Ending Requested';
const ENDED = 'Ended';

const CAMPAIGN_STATUSES = {
  CREATING,
  CREATION_FAILED,
  SETUP_PROGRESS,
  RUNNING,
  PAUSED,
  ENDING_REQUESTED,
  ENDED,
};

export const allStatusesEnum = convertToEnumWithOptions(CAMPAIGN_STATUSES);

export const publicStatuses = [
  allStatusesEnum.SETUP_PROGRESS,
  allStatusesEnum.RUNNING,
  allStatusesEnum.PAUSED,
  allStatusesEnum.ENDED,
];

export const filterStatuses = [
  allStatusesEnum.SETUP_PROGRESS,
  allStatusesEnum.RUNNING,
  allStatusesEnum.PAUSED,
  allStatusesEnum.ENDING_REQUESTED,
  allStatusesEnum.ENDED,
];

export const campaignFilterFields = ['name', 'nCode'];

export const campaignSelectLabel = object => {
  return `${object.nCode} - ${object.name}`;
};

export const STATUS_COLOR_MAP = {
  [SETUP_PROGRESS]: COLORS.INFO,
  [RUNNING]: COLORS.SUCCESS,
  [PAUSED]: COLORS.WARNING,
  [ENDED]: COLORS.SECONDARY,
};

export const adOpsDelayedStartReasonsOptions = [
  'Not meeting SLA',
  'AdOps delay',
  'Incomplete briefing',
  'Brand Delay',
  'Creative Delay',
  'Not according Ad Specs',
  'Wrong Media Set',
  'Wrong LPs',
  'LP not Live',
  'Wrong Text/Copy',
  'Missing Copy',
  'Non Eligible SKUs',
  'Highlighted SKUs blank',
  'Budget Requirement not meet',
  'Wrong/Missing Supplier Code',
  'Wrong Campaign Objective (PG or SB)',
  'New brand on CCP',
  'Non-compliant campaign (Campaign Packages)',
  'Other',
].map(convertToOption);

export const pcDelayedStartReasonsOptions = [
  'Product availability issue (caused by brand/external factors)',
  'No/Delayed/Outstanding feedback from brand',
  'Budget frozen by brand',
  'Contractual bottlenecks',
  'Product availability issue (caused by Zalando)',
  'Zalando-internal bottlenecks',
  'ZMS Creative dependencies',
  'ZMS Internal management',
  'CXM',
].map(convertToOption);

export const underPerformanceReasonsOptions = [
  'Number of Countries < 5',
  'Number of Countries > 6',
  'Less than 50 SKUs',
  'Between 50 - 100 SKUs',
  'More than 100 SKUs',
  'Brand / SKU Performance',
  'Discounted SKUs',
  'Category Performance',
  'Creative Performance',
  'Competition',
  'CXM',
  'Frontloading',
  'High Budget',
  'High Target',
  'Low Max Bid',
  'Low Page Position',
  'Narrow Audience Targeting',
  'Non-DACH campaign',
  'Quality Score',
  'Short Runtime',
  'Wrong Supplier',
].map(convertToOption);

export const boolOptions = [
  { label: 'Yes', value: true },
  { label: 'No', value: false },
];

export const merchantExclusivityOptions = [
  { label: 'Optimal', value: 'Optimal' },
  { label: 'Full', value: 'Full' },
];

export const csvColumns = users => [
  { accessor: 'id', label: 'Id' },
  { accessor: 'nCode', label: 'NCode' },
  { accessor: 'name', label: 'Name' },
  { accessor: 'opportunity.salesUnit', label: 'Sales Unit' },
  { accessor: 'opportunity.salesTeam', label: 'Sales Team' },
  { accessor: 'opportunity.useCase', label: 'Use Case' },
  { accessor: 'owner.firstName', label: 'Owner First Name' },
  { accessor: 'owner.lastName', label: 'Owner Last Name' },
  { accessor: 'status', label: 'Status' },
  { accessor: 'adOpsDelayedStartReasons', label: 'Campaign Delay Reasons' },
  { accessor: 'pcDelayedStartReasons', label: 'Briefing Delay Reasons' },
  { accessor: 'budgets["Media Onsite"]', label: 'Budget Media Onsite' },
  { accessor: 'budgets["Media Offsite"]', label: 'Budget Media Offsite' },
  { accessor: 'budgets.Creative', label: 'Budget Creative' },
  {
    accessor: 'budgets["Consumer Insights"]',
    label: 'Budget Consumer Insights',
  },
  { accessor: 'budgets.Data', label: 'Budget Data' },
  { accessor: 'budgets.Other', label: 'Budget Other' },
  { accessor: 'spentBudgets["Media Onsite"]', label: 'Spent Media Onsite' },
  { accessor: 'spentBudgets["Media Offsite"]', label: 'Spent Media Offsite' },
  { accessor: 'spentBudgets.Creative', label: 'Spent Creative' },
  {
    accessor: 'spentBudgets["Consumer Insights"]',
    label: 'Spent Consumer Insights',
  },
  { accessor: 'spentBudgets.Data', label: 'Spent Data' },
  { accessor: 'spentBudgets.Other', label: 'Spent Other' },
  { accessor: 'startDatePlan', label: 'Planned Start Date' },
  { accessor: 'endDatePlan', label: 'Planned End Date' },
  { accessor: 'startDateIs', label: 'Actual Start Date' },
  { accessor: 'endDateIs', label: 'Actual End Date' },
  { accessor: 'opportunity.accountSfId', label: 'Account ID' },
  { accessor: 'brands[*].name', label: 'Brand Names' },
  { accessor: 'brands[*].code', label: 'Brand Codes' },
  {
    label: 'Genders',
    accessor: campaign => getUniqueGenders(campaign.campaignLines),
  },
  {
    label: 'Co-Invest',
    accessor: campaign => getCoinvestSum(campaign.campaignLines),
  },
  {
    label: 'Draft Lines',
    accessor: campaign =>
      getNumberOfLinesWithStatus(
        campaign.campaignLines,
        campaignLineStatuses.DRAFT,
      ),
  },
  {
    label: 'Ready Lines',
    accessor: campaign =>
      getNumberOfLinesWithStatus(
        campaign.campaignLines,
        campaignLineStatuses.READY,
      ),
  },
  {
    label: 'Pushed Lines',
    accessor: campaign =>
      getNumberOfLinesWithStatus(
        campaign.campaignLines,
        campaignLineStatuses.PUSHED,
      ),
  },
  {
    label: 'Locked Lines',
    accessor: campaign =>
      getNumberOfLinesWithStatus(
        campaign.campaignLines,
        campaignLineStatuses.LOCKED,
      ),
  },
  { accessor: 'opportunity.id', label: 'Opportunity Id' },
  { accessor: 'opportunity.stage', label: 'Opportunity Stage' },
  { accessor: 'opportunity.salesChannel', label: 'Opportunity Sales Channel' },
  { accessor: 'quote.id', label: 'Quote Id' },
  { accessor: 'created', label: 'Created' },
  { accessor: 'createdBy', label: 'Created By' },
  { accessor: 'lastModified', label: 'Last Modified' },
  { accessor: 'lastModifiedBy', label: 'Last Modified By' },
  {
    accessor: campaign =>
      getCollaboratorNumbers(campaign.collaboratorIds, users),
    label: 'Collaborator Numbers',
  },
  { accessor: 'quote.flexibilityTerms', label: 'Flexibility Terms' },
  { accessor: 'quote.allowedFlexibilities', label: 'Allowed Flexibilities' },
  {
    accessor: campaign => getYesNoValue(campaign.hasScalableBudget),
    label: 'Scalable Budget',
  },
  {
    accessor: campaign => getUserNumber(campaign.opsOnsiteId, users),
    label: 'Ops Onsite Number',
  },
  {
    accessor: campaign => getUserNumber(campaign.opsCreativeId, users),
    label: 'Ops Creative Number',
  },
  { accessor: 'commercialIncentive', label: 'Commercial Incentive' },
  { accessor: 'opportunity.campaignObjective', label: 'Campaign Objective' },
  { accessor: 'targetKpi', label: 'Target KPI' },
  { accessor: 'targetKpiValue', label: 'Target KPI Value' },
  {
    accessor: campaign => getOpsCompliancePercentage(campaign.campaignLines),
    label: 'Compliancy %',
  },
];
